import React, { useContext, useEffect } from 'react';
import { IMediaCollectionState } from "../../type/media-hub.types";
import { useBaseComponent } from "../../../../base/base-component";
import {
    deleteDocument, deleteMultiple, download, downloadMultiple, fetchMetaFields, getMedia,
    maximumDeleteLimit, mediaDocumentMapper, uploadEventMedia
} from "../../action/media-hub.actions";
import { message } from "antd";
import MediaCollectionView from "./media-collection.view";
import UserLoginContext from "../../../../context/userLoginContext";
import { Document } from "../../../../domains/models/document";
import { useLocation } from "react-router-dom";
import { getDocumentKey, mapPathWithCategories } from "../../utils/utils";
import { FindDocumentsParams } from "../../../../server/document";
import { getUIMetaFields } from "../../../common/upload-document-modal/utils/document-utils";
import '../../../../styles/component/media-hub.scss'

const MediaCollection: React.FC<{ searchTerm?: string }> = ({searchTerm}) => {

    const {user} = useContext(UserLoginContext);
    const isAdmin = user.isAdmin;
    const location = useLocation();
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const eventTab = mapPathWithCategories(pathSegments);
    const eventId = location.state?.eventId ?? null;


    const {state, updateState} = useBaseComponent<{}, IMediaCollectionState>({}, {
        initialState: () => ({
            data: {
                docs: []
            },
            internal: {
                categoryActive: eventTab,
                UIMetaFields: [],
                pagination: {
                    hasNext: false,
                    hasPrevious: false,
                    pageNumber: 0,
                    pageSize: 20,
                    isLoading: false,
                    maxItems: 60
                }
            }
        })
    });

    useEffect(() => {
        loadMedia();
    }, [location, searchTerm])


    const loadMedia = async () => {
        try {
            const res = await fetchMediaPage(0);
            const enumKey = getDocumentKey(eventTab)!;

            //fetch meta fields
            const metRes = await fetchMetaFields(enumKey);
            const metaValues = getUIMetaFields(metRes);

            const docs = mediaDocumentMapper(res);

            updateState('data.docs', docs);
            updateState('internal', {
                ...state.internal,
                categoryActive: eventTab,
                UIMetaFields: metaValues,
                pagination: {
                    ...state.internal.pagination,
                    hasNext: res.length === state.internal.pagination.pageSize
                }
            });
        } catch (err) {
            console.error("Failed to fetch media documents:", err);
        }
    };


    const downloadFile = (fileObj: Blob | any, fileName: string) => {
        const link = document.createElement("a");
        const url = URL.createObjectURL(fileObj);
        link.href = url;
        link.download = fileName || "downloaded-file";
        link.click();
        URL.revokeObjectURL(url);
    };

    async function handleDownload(item: Document) {
        try {
            const response = await download(item.id);
            if (response instanceof Blob) {
                downloadFile(response, item.originalFileName);
            } else {
                console.error("The response is not a valid Blob.");
            }
        } catch (e) {
            console.log('encountered error while downloading ')
        }
    }

    async function handleMultipleDownload(ids: string[]) {
        try {
            const response = await downloadMultiple(ids);
            const zipFile = new Blob([response], {type: 'application/zip'});
            downloadFile(zipFile, 'download.zip');
        } catch (e) {
            console.log('failed to download selected files', e)
        }
    }

    async function handleDelete(id: string) {
        try {
            await deleteDocument(id);
            const updatedDocs = state.data.docs.filter((item) => item.id !== id)
            updateState('data.docs', updatedDocs);
        } catch (e) {
            console.log('encountered error while deleting ', e)
        }
    }

    async function handleMultipleDelete(ids: string[]) {
        try {
            if (ids.length > maximumDeleteLimit) {
                message.error(`You can delete up to ${maximumDeleteLimit} files at a time.`)
                throw new Error(`Error!,You can delete up to ${maximumDeleteLimit} files at a time.`);
            }
            await deleteMultiple(ids);
            const updatedDocs = state.data.docs.filter(doc => !ids.includes(doc.id));
            updateState('data.docs', updatedDocs)

        } catch (e) {
            console.log('Could not delete multiple files ', e)
        }
    }

    async function fetchMediaPage(pageNumber: number) {
        let res;
        let params: FindDocumentsParams = {search: searchTerm}
        if (eventId) {
            params = {...params, eventId: eventId}
        } else if (eventTab) {
            const enumKey = getDocumentKey(eventTab);
            params = {...params, purpose: 'GALLERY', documentType: enumKey}
        } else {
            params = {...params, purpose: 'GALLERY'}
        }
        res = await getMedia(params, pageNumber);
        return res;
    }

    async function loadMoreMedia() {
        const maxFiles = state.internal.pagination.maxItems;
        const currentPage = state.internal.pagination.pageNumber;

        updateState('internal.pagination.isLoading', true);

        try {

            const res = await fetchMediaPage(currentPage + 1); // fetch next data
            const mappedDocs = mediaDocumentMapper(res);
            let totalDocs = [...state.data.docs, ...mappedDocs];

            // Sliding window: keep only last maxFiles items
            if (totalDocs.length > maxFiles) {
                const excess = totalDocs.length - maxFiles;
                totalDocs = totalDocs.slice(excess);
            }
            console.log(res.length === state.internal.pagination.pageSize, 'hasNext');
            updateState('data.docs', totalDocs);
            updateState('internal.pagination.hasNext', res.length === state.internal.pagination.pageSize);
            updateState('internal.pagination.hasPrevious', true);
            updateState('internal.pagination.pageNumber', currentPage + 1); // move to next page

        } catch (e) {
            console.log('Error while loading more media', e);
        } finally {
            updateState('internal.pagination.isLoading', false);
        }
    }

    async function loadPreviousMedia() {
        const {pageNumber, maxItems} = state.internal.pagination;

        // No previous page
        if (pageNumber <= 0) return;

        // if we never exceeded maxItems, nothing was trimmed
        if (state.data.docs.length < maxItems) {
            updateState('internal.pagination.hasPrevious', false);
            return;
        }

        const previousPage = pageNumber - 1;

        updateState('internal.pagination.isLoading', true);

        try {
            const res = await fetchMediaPage(previousPage);
            const mappedDocs = mediaDocumentMapper(res);
            let totalDocs = [...mappedDocs, ...state.data.docs];

            // Sliding window only needed here
            if (totalDocs.length > maxItems) {
                totalDocs = totalDocs.slice(0, maxItems);
            }


            updateState('data.docs', totalDocs);
            updateState('internal.pagination.pageNumber', previousPage);
            updateState('internal.pagination.hasNext', true);

        } catch (e) {
            console.log('Error while loading previous media', e);
        } finally {
            updateState('internal.pagination.isLoading', false);
        }
    }

    function addUploadedMedia(files: Document[]) {
        const mappedDocs = mediaDocumentMapper(files);
        const addedFiles = [...mappedDocs, ...state.data.docs];
        updateState('data.docs', addedFiles)
    }


    async function handleUploadedMedia(res: Document[]) {
        if (eventId) {
            const documentIds = res.map((item) => item.id);
            //map response with event-id
            await uploadEventMedia(eventId, documentIds)
        }
        addUploadedMedia(res);
        return;
    }


    return (
        <MediaCollectionView
            files={state.data.docs}
            isAdmin={isAdmin}
            paginationControls={{
                hasNext: state.internal.pagination.hasNext,
                hasPrevious: state.internal.pagination.hasPrevious,
                loadNext: loadMoreMedia,
                loadPrevious: loadPreviousMedia,
                isLoading: state.internal.pagination.isLoading,
            }}
            UIMetaFields={state.internal.UIMetaFields}
            categorySelected={state.internal.categoryActive}
            handleDownload={handleDownload}
            handleDelete={handleDelete}
            handleMultipleDownload={handleMultipleDownload}
            handleMultipleDelete={handleMultipleDelete}
            handleUploadedMedia={handleUploadedMedia}
        />
    )
}

export default MediaCollection;
